import {BaseQueryApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {addBreadcrumb, captureException} from '@sentry/browser';

const RETRY_DELAY = 2000;
const INFRASTRUCTURE_ERRORS_CODES = [502, 503, 504];
const DEFAULT_HTTP_METHOD = 'GET';
const SENTRY_FINGERPRINT_PREFIX = 'rtk-query';

type NumericFetchBaseQueryError = Extract<FetchBaseQueryError, {status: number}>;

const captureBaseQueryError = (
  error: NumericFetchBaseQueryError,
  request: Pick<FetchArgs, 'method'>,
  api: Pick<BaseQueryApi, 'endpoint'>
) => {
  const statusTag = error.status.toString();

  captureException(new Error(`RTK Query request failed with status ${statusTag}`), {
    fingerprint: [SENTRY_FINGERPRINT_PREFIX, api.endpoint, statusTag],
    tags: {
      endpoint: api.endpoint,
      method: request.method ?? DEFAULT_HTTP_METHOD,
      status: statusTag,
    },
  });
};

/**
 * Wraps fetchBaseQuery with a single fixed-delay retry on gateway infrastructure
 * errors (502/503/504) for queries only — mutations are never retried to avoid
 * duplicate side effects. Application errors are never retried. Final 5xx
 * responses are reported to Sentry.
 */
export const createPriceReportBaseQuery = (options: Parameters<typeof fetchBaseQuery>[0]) => {
  const fetchBaseQueryInstance = fetchBaseQuery(options);

  return async (...baseQueryArgs: Parameters<typeof fetchBaseQueryInstance>) => {
    const [args, api] = baseQueryArgs;
    let result = await fetchBaseQueryInstance(...baseQueryArgs);

    if (result.error) {
      const status = Number(result.meta?.response?.status ?? result.error.status);
      if (api.type === 'query' && INFRASTRUCTURE_ERRORS_CODES.includes(status)) {
        const url = typeof args === 'string' ? args : args.url;
        addBreadcrumb({
          category: 'api',
          message: `Retrying API request ${url}, because response status is ${status}`,
          level: 'error',
        });
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        result = await fetchBaseQueryInstance(...baseQueryArgs);
      }
    }

    if (result.error && typeof result.error.status === 'number' && result.error.status >= 500) {
      captureBaseQueryError(result.error, typeof args === 'string' ? {} : args, api);
    }

    return result;
  };
};
